import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Printer, Link2, Send, Users, Search, Loader2, CheckCircle2,
  XCircle, Mail, Trash2, Copy, QrCode, CalendarPlus, Download, Eye,
  MousePointerClick, ListChecks, Save, Rocket, EyeOff, AlertTriangle, PartyPopper, Clock,
  Bell, HelpCircle,
} from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { FlyerCanvas } from '../components/flyer/FlyerCanvas';
import { CustomFlyerCard, flyerImageUrl } from '../components/events/CustomFlyerCard';
import { EventForm } from '../components/events/EventForm';
import { EmailPreviewDialog } from '../components/events/EmailPreviewDialog';
import { SchedulePanel } from '../components/events/SchedulePanel';
import { QrShareCard } from '../components/events/QrShareCard';
import { NudgeComposer } from '../components/events/NudgeComposer';
import { EventStatusBadge } from '../components/events/EventStatusBadge';
import { AddToListDialog } from '../components/contacts/ListDialogs';
import { PhotoManager } from '../components/landing/PhotoManager';
import { downloadCsv, slugifyName } from '../lib/csv';

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [flyerVer, setFlyerVer] = useState(0);

  const loadEvent = useCallback(() => {
    api.get(`/events/${id}`).then((r) => setEvent(r.data)).catch(() => toast.error('Event not found')).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { loadEvent(); }, [loadEvent]);

  const copyLink = (url) => { navigator.clipboard.writeText(url); toast.success('Link copied!'); };

  const setStatus = async (status) => {
    setStatusSaving(true);
    try {
      const r = await api.put(`/events/${id}`, { status });
      setEvent(r.data);
      toast.success(status === 'published' ? 'Published — guests can now open the link.' : 'Moved to draft. Hidden from guests.');
    } catch (e) {
      toast.error('Could not update status');
    } finally {
      setStatusSaving(false);
    }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-10 w-48" /><Skeleton className="h-96 rounded-2xl" /></div>;
  if (!event) return null;

  const isDraft = event.effective_status === 'draft';

  return (
    <div data-testid="event-detail-page">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap no-print">
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate('/app/events')} className="gap-2 -ml-2">
            <ArrowLeft className="h-4 w-4" /> Events
          </Button>
          <EventStatusBadge status={event.effective_status} />
        </div>
        <div className="flex items-center gap-2">
          {isDraft ? (
            <Button onClick={() => setStatus('published')} disabled={statusSaving} className="gap-2" data-testid="publish-event-button">
              {statusSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />} Publish
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => setStatus('draft')} disabled={statusSaving} className="gap-2" data-testid="unpublish-event-button">
              {statusSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <EyeOff className="h-4 w-4" />} Unpublish
            </Button>
          )}
          <a href={event.google_calendar_url} target="_blank" rel="noreferrer" data-testid="host-gcal-button">
            <Button variant="secondary" className="gap-2"><CalendarPlus className="h-4 w-4" /> Google Cal</Button>
          </a>
          <a href={`${process.env.REACT_APP_BACKEND_URL}${event.ics_url}`} data-testid="host-ics-button">
            <Button variant="secondary" className="gap-2"><Download className="h-4 w-4" /> .ics</Button>
          </a>
          <Button variant="secondary" onClick={() => copyLink(event.event_invite_url || event.event_url)} className="gap-2" data-testid="copy-rsvp-link-button">
            <Link2 className="h-4 w-4" /> Copy RSVP link
          </Button>
          <Button variant="secondary" onClick={() => setPreviewOpen(true)} className="gap-2" data-testid="preview-email-button">
            <Mail className="h-4 w-4" /> Preview email
          </Button>
          <Button variant="secondary" onClick={() => setShowQr((v) => !v)} className="gap-2" data-testid="toggle-qr-button">
            <QrCode className="h-4 w-4" /> QR codes
          </Button>
          <Button onClick={() => window.print()} className="gap-2" data-testid="flyer-print-button">
            <Printer className="h-4 w-4" /> Print flyer
          </Button>
        </div>
      </div>

      {showQr && (
        <div className="mb-5 rounded-2xl border border-border bg-card p-5 no-print" data-testid="event-qr-section">
          <div className="flex items-center gap-2 mb-4">
            <QrCode className="h-[18px] w-[18px] text-primary" />
            <h3 className="font-display text-lg font-semibold">Share via QR</h3>
            <span className="text-sm text-muted-foreground">— scan or save to share this event</span>
          </div>
          <QrShareCard event={event} />
        </div>
      )}

      {isDraft && (
        <div className="mb-5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 flex items-center gap-3 no-print" data-testid="draft-banner">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <div className="flex-1 text-sm">
            <span className="font-semibold text-foreground">This event is a draft.</span>
            <span className="text-muted-foreground"> Guests can&apos;t open the link and you can&apos;t send invites until you publish.</span>
          </div>
          <Button size="sm" onClick={() => setStatus('published')} disabled={statusSaving} className="gap-2" data-testid="draft-banner-publish-button">
            <Rocket className="h-4 w-4" /> Publish
          </Button>
        </div>
      )}

      <EmailPreviewDialog event={event} message="" open={previewOpen} onOpenChange={setPreviewOpen} />

      <Tabs defaultValue="flyer">
        <TabsList className="no-print">
          <TabsTrigger value="flyer" data-testid="tab-flyer">Flyer</TabsTrigger>
          <TabsTrigger value="send" data-testid="tab-send">Send invites</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
          <TabsTrigger value="guests" data-testid="tab-guests">Guests</TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-photos">Photos</TabsTrigger>
          <TabsTrigger value="edit" data-testid="tab-edit">Edit</TabsTrigger>
        </TabsList>

        <TabsContent value="flyer" className="mt-6">
          {event.use_custom_flyer && event.has_custom_flyer ? (
            <div className="print-area max-w-[800px] mx-auto" data-testid="custom-flyer-view">
              <img src={flyerImageUrl(event, flyerVer)} alt={event.title}
                className="w-full rounded-2xl border border-border shadow-[0_20px_60px_rgba(17,24,39,0.12)]" />
            </div>
          ) : (
            <FlyerCanvas event={event} />
          )}
          <div className="max-w-[800px] mx-auto mt-5 space-y-3 no-print">
            <CustomFlyerCard event={event}
              onChange={(data) => { setEvent(data); setFlyerVer((v) => v + 1); }} ver={flyerVer} />
            <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-4 flex items-center gap-3" data-testid="rsvp-link-card">
              <PartyPopper className="h-5 w-5 text-primary flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">Shareable RSVP link</div>
                <div className="text-xs text-muted-foreground truncate">{event.event_invite_url || event.event_url}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">Anyone with this link can RSVP &amp; see the address — share it directly (text, social, group chat).</div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => copyLink(event.event_invite_url || event.event_url)} data-testid="copy-rsvp-link-inline"><Copy className="h-4 w-4" /></Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3">
                <Eye className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">Public preview page</div>
                  <div className="text-xs text-muted-foreground truncate">{event.event_url}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">Preview only — no address, no RSVP.</div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => copyLink(event.event_url)}><Copy className="h-4 w-4" /></Button>
              </div>
              <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3">
                <Mail className="h-5 w-5 text-accent flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">Email signup page</div>
                  <div className="text-xs text-muted-foreground truncate">{event.signup_url}</div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => copyLink(event.signup_url)}><Copy className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="send" className="mt-6">
          <SendPanel event={event} onSent={loadEvent} />
        </TabsContent>

        <TabsContent value="schedule" className="mt-6">
          <SchedulePanel event={event} />
        </TabsContent>

        <TabsContent value="guests" className="mt-6">
          <GuestsPanel event={event} />
        </TabsContent>

        <TabsContent value="photos" className="mt-6">
          <div className="rounded-2xl border border-border bg-card p-6">
            <PhotoManager scope="event" eventId={event.id} title="Event gallery"
              hint="Add photos from this event. They appear in a gallery on the public event page, and the first photo can be used as the flyer cover on your landing page." />
          </div>
        </TabsContent>

        <TabsContent value="edit" className="mt-6">
          <EditPanel event={event} onSaved={loadEvent} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const EditPanel = ({ event, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const save = async (data) => {
    setSaving(true);
    try {
      await api.put(`/events/${event.id}`, data);
      toast.success('Event updated');
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update event');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div data-testid="event-edit-panel">
      <EventForm initial={event} onSubmit={save} submitLabel="Save changes" submitting={saving} />
    </div>
  );
};

const SendPanel = ({ event, onSent }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const [lists, setLists] = useState([]);
  const [saveListOpen, setSaveListOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [skipDup, setSkipDup] = useState(true);
  const [onlyNon, setOnlyNon] = useState(false);
  const [invitedIds, setInvitedIds] = useState(new Set());
  const [scheduleLater, setScheduleLater] = useState(false);
  const [sendAt, setSendAt] = useState('');

  useEffect(() => {
    api.get(`/events/${event.id}/invites`)
      .then((r) => setInvitedIds(new Set(r.data.map((i) => i.contact_id))))
      .catch(() => {});
  }, [event.id, results]);

  useEffect(() => {
    const params = search.trim() ? { search: search.trim() } : {};
    const t = setTimeout(() => {
      api.get('/contacts', { params }).then((r) => setContacts(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { api.get('/groups').then((r) => setLists(r.data)).catch(() => {}); }, []);

  const loadFromList = async (listId) => {
    try {
      const r = await api.get(`/groups/${listId}/members`);
      const ids = r.data.members.map((m) => m.id);
      setSelected(new Set(ids));
      toast.success(`Loaded ${ids.length} from list`);
    } catch (e) { toast.error('Could not load list'); }
  };

  const allSelected = contacts.length > 0 && contacts.every((c) => selected.has(c.id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) contacts.forEach((c) => next.delete(c.id));
    else contacts.forEach((c) => next.add(c.id));
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const send = async () => {
    if (selected.size === 0) { toast.error('Select at least one contact'); return; }
    if (scheduleLater) {
      if (!sendAt) { toast.error('Pick a date and time to send'); return; }
      setSending(true);
      try {
        await api.post(`/events/${event.id}/schedule-invite`, {
          contact_ids: [...selected], custom_message: message, send_at: sendAt,
        });
        toast.success('Invitation scheduled — see the Schedule tab');
        setSelected(new Set());
        onSent && onSent();
      } catch (err) { toast.error(err.response?.data?.detail || 'Could not schedule invite'); }
      finally { setSending(false); }
      return;
    }
    setSending(true);
    setResults(null);
    try {
      const res = await api.post(`/events/${event.id}/send`, {
        contact_ids: [...selected], custom_message: message,
        skip_duplicates: skipDup, only_non_responders: onlyNon,
      });
      setResults(res.data);
      if (res.data.background) {
        const n = res.data.queued;
        toast.success(`Sending to ${n} ${n === 1 ? 'person' : 'people'} in the background — this can take a minute or two. You can leave this page; the invite count updates as they go.`);
        // Nudge the invite count a few times while the background send runs.
        onSent && onSent();
        setTimeout(() => onSent && onSent(), 8000);
        setTimeout(() => onSent && onSent(), 25000);
      } else {
        const extra = [];
        if (res.data.skipped_already_invited) extra.push(`${res.data.skipped_already_invited} already invited`);
        if (res.data.skipped_responded) extra.push(`${res.data.skipped_responded} already responded`);
        toast.success(`Sent ${res.data.sent} invite${res.data.sent !== 1 ? 's' : ''}${res.data.failed ? `, ${res.data.failed} failed` : ''}${extra.length ? ` · skipped ${extra.join(', ')}` : ''}`);
        onSent && onSent();
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not send invites'); }
    finally { setSending(false); }
  };

  const isDraft = event.effective_status === 'draft';
  const selectedInvitedCount = [...selected].filter((id) => invitedIds.has(id)).length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 rounded-2xl border border-border bg-card overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search contacts" className="h-11 pl-9" data-testid="send-search-input" />
            </div>
            {lists.length > 0 && (
              <Select onValueChange={loadFromList}>
                <SelectTrigger className="h-11 w-44" data-testid="send-load-list"><SelectValue placeholder="Load from list" /></SelectTrigger>
                <SelectContent>
                  {lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} ({(l.contact_ids || []).length})</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex items-center justify-between mt-3 px-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="send-select-all" />
              <span className="text-sm font-medium">Select all ({contacts.length})</span>
            </label>
            {selected.size > 0 && (
              <button type="button" onClick={() => setSaveListOpen(true)} className="text-xs font-medium text-primary flex items-center gap-1" data-testid="save-selection-as-list">
                <Save className="h-3.5 w-3.5" /> Save {selected.size} as list
              </button>
            )}
          </div>
        </div>
        <div className="max-h-[420px] overflow-auto">
          {loading ? (
            <div className="p-4 space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 rounded-lg" />)}</div>
          ) : contacts.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground text-center">No contacts found. Import contacts first.</p>
          ) : contacts.map((c) => (
            <label key={c.id} className={`flex items-center gap-3 px-4 py-2.5 border-b border-border last:border-0 cursor-pointer transition-colors ${selected.has(c.id) ? 'bg-primary/5' : 'hover:bg-secondary/40'}`} data-testid="send-contact-row">
              <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleOne(c.id)} />
              <div className="min-w-0">
                <div className="text-sm font-medium">{`${c.first_name} ${c.last_name}`.trim() || c.email}</div>
                <div className="text-xs text-muted-foreground">{c.email}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="font-display text-lg font-semibold mb-3">Send invites</h3>
          <div className="space-y-1.5 mb-4">
            <label className="text-sm font-medium">Personal note (optional)</label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="Add a personal message to your invite…" data-testid="send-message-input" />
          </div>

          <div className="space-y-2.5 mb-4 rounded-xl border border-border bg-secondary/40 p-3">
            <label className="flex items-center justify-between gap-3 cursor-pointer">
              <span className="text-sm">Skip already-invited contacts</span>
              <Switch checked={skipDup} onCheckedChange={setSkipDup} data-testid="send-skip-duplicates-toggle" />
            </label>
            <label className="flex items-center justify-between gap-3 cursor-pointer">
              <span className="text-sm">Only send to non-responders</span>
              <Switch checked={onlyNon} onCheckedChange={setOnlyNon} data-testid="send-only-nonresponders-toggle" />
            </label>
            <label className="flex items-center justify-between gap-3 cursor-pointer pt-2 border-t border-border/60">
              <span className="text-sm flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-muted-foreground" /> Schedule for later</span>
              <Switch checked={scheduleLater} onCheckedChange={setScheduleLater} data-testid="send-schedule-later-toggle" />
            </label>
            {scheduleLater && (
              <div className="space-y-1.5 pt-1" data-testid="send-schedule-datetime">
                <label className="text-xs font-medium text-muted-foreground">Send date & time{event.timezone ? ` (${event.timezone})` : ''}</label>
                <Input type="datetime-local" value={sendAt} onChange={(e) => setSendAt(e.target.value)} data-testid="send-schedule-at-input" />
              </div>
            )}
          </div>

          <div className="text-sm text-muted-foreground mb-3">
            {selected.size} recipient{selected.size !== 1 ? 's' : ''} selected
            {selectedInvitedCount > 0 && (
              <span className="text-amber-600 dark:text-amber-400"> · {selectedInvitedCount} already invited{skipDup ? ' (will skip)' : ' (will resend)'}</span>
            )}
          </div>

          {isDraft && (
            <div className="mb-3 text-xs rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-700 dark:text-amber-300 px-3 py-2" data-testid="send-draft-notice">
              Publish this event (top of page) before sending — draft links don&apos;t open for guests.
            </div>
          )}

          <Button variant="outline" onClick={() => setPreviewOpen(true)} className="w-full h-11 gap-2 mb-2" data-testid="send-preview-email-button">
            <Eye className="h-4 w-4" /> Preview email
          </Button>
          <Button onClick={send} disabled={sending || selected.size === 0 || isDraft} className="w-full h-11 gap-2" data-testid="flyer-send-invites-button">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : scheduleLater ? <Clock className="h-4 w-4" /> : <Send className="h-4 w-4" />}
            {sending ? (scheduleLater ? 'Scheduling…' : 'Sending…') : isDraft ? 'Publish to send' : scheduleLater ? `Schedule ${selected.size || ''} invite${selected.size !== 1 ? 's' : ''}` : `Send ${selected.size || ''} invite${selected.size !== 1 ? 's' : ''}`}
          </Button>
          <EmailPreviewDialog event={event} message={message} open={previewOpen} onOpenChange={setPreviewOpen} />
        </div>

        {results && (
          results.background ? (
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="send-results">
              <h3 className="font-medium mb-2 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin text-primary" /> Sending in progress</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Your invitations are going out to <span className="font-medium text-foreground">{results.queued}</span> {results.queued === 1 ? 'person' : 'people'} in the background. Large lists can take a minute or two — you can safely leave this page. The recipient count updates as they send.
              </p>
              <div className="flex flex-wrap gap-3">
                {results.skipped_already_invited > 0 && <div className="flex items-center gap-1.5 text-sm text-muted-foreground"><Mail className="h-4 w-4" /> {results.skipped_already_invited} already invited</div>}
                {results.skipped_responded > 0 && <div className="flex items-center gap-1.5 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4" /> {results.skipped_responded} already responded</div>}
                {results.skipped_unsubscribed > 0 && <div className="flex items-center gap-1.5 text-sm text-muted-foreground"><XCircle className="h-4 w-4" /> {results.skipped_unsubscribed} unsubscribed</div>}
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="send-results">
              <h3 className="font-medium mb-3">Results</h3>
              <div className="flex flex-wrap gap-3 mb-3">
                <div className="flex items-center gap-1.5 text-sm"><CheckCircle2 className="h-4 w-4 text-primary" /> {results.sent} sent</div>
                {results.failed > 0 && <div className="flex items-center gap-1.5 text-sm"><XCircle className="h-4 w-4 text-destructive" /> {results.failed} failed</div>}
                {results.skipped_already_invited > 0 && <div className="flex items-center gap-1.5 text-sm text-muted-foreground"><Mail className="h-4 w-4" /> {results.skipped_already_invited} already invited</div>}
                {results.skipped_responded > 0 && <div className="flex items-center gap-1.5 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4" /> {results.skipped_responded} already responded</div>}
              </div>
              <div className="max-h-40 overflow-auto space-y-1">
                {results.details.map((d, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1">
                    <span className="text-muted-foreground truncate">{d.email}</span>
                    {d.status === 'sent' ? <Badge variant="secondary" className="text-primary">sent</Badge> : <Badge variant="secondary" className="text-destructive">failed</Badge>}
                  </div>
                ))}
              </div>
            </div>
          )
        )}
      </div>
      <AddToListDialog open={saveListOpen} onOpenChange={setSaveListOpen} contactIds={[...selected]} onDone={() => { api.get('/groups').then((r) => setLists(r.data)).catch(() => {}); }} />
    </div>
  );
};

const NudgeList = ({ items, type, nudged, onCompose }) => {
  const remaining = items.filter((p) => !nudged.has((p.email || '').toLowerCase()));
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">{type === 'maybe' ? 'No “maybe” replies.' : 'Everyone invited has replied.'}</p>;
  }
  return (
    <div className="space-y-1.5">
      <div className="flex justify-end">
        <Button size="sm" variant="secondary" className="gap-1.5 h-8" disabled={remaining.length === 0}
          onClick={() => onCompose(remaining.map((p) => ({ name: p.name, email: p.email })))} data-testid={`nudge-all-${type}`}>
          <Bell className="h-3.5 w-3.5" /> Nudge all ({remaining.length})
        </Button>
      </div>
      {items.map((p) => {
        const done = nudged.has((p.email || '').toLowerCase());
        return (
          <div key={p.id || p.email} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-secondary/40" data-testid="nudge-row">
            <div className="min-w-0"><div className="text-sm font-medium truncate">{p.name || p.email}</div><div className="text-xs text-muted-foreground truncate">{p.email}</div></div>
            {done ? <span className="text-xs text-primary flex items-center gap-1 flex-shrink-0"><CheckCircle2 className="h-3.5 w-3.5" /> Nudged</span> : (
              <Button size="sm" variant="ghost" className="gap-1.5 h-8 text-primary flex-shrink-0"
                onClick={() => onCompose([{ name: p.name, email: p.email }])} data-testid="nudge-one">
                <Send className="h-3.5 w-3.5" /> Nudge
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};

const GuestsPanel = ({ event }) => {
  const [invites, setInvites] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [nudged, setNudged] = useState(() => new Set());
  const [composer, setComposer] = useState(null); // { recipients }

  const load = useCallback(async (silent) => {
    if (silent) setRefreshing(true);
    try {
      const [i, r] = await Promise.all([
        api.get(`/events/${event.id}/invites`),
        api.get(`/signups/event/${event.id}/rsvps`),
      ]);
      setInvites(i.data); setRsvps(r.data);
    } catch (e) { /* noop */ }
    finally { setLoading(false); setRefreshing(false); }
  }, [event.id]);

  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  const rsvpBadge = (s) => s === 'yes' ? 'text-primary' : s === 'no' ? 'text-destructive' : 'text-muted-foreground';

  if (loading) return <Skeleton className="h-64 rounded-2xl" />;

  const fields = event.rsvp_fields || [];
  const fieldLabel = (k) => (fields.find((f) => f.key === k)?.label) || k;
  const sharedFields = fields.filter((f) => f.share_responses);

  const yes = rsvps.filter((r) => r.status === 'yes');
  const maybe = rsvps.filter((r) => r.status === 'maybe');
  const no = rsvps.filter((r) => r.status === 'no');
  const headcount = yes.reduce((sum, r) => sum + Math.max(Number(r.guests) || 1, 1), 0);

  const renderVal = (v) => Array.isArray(v) ? v.join(', ') : String(v);
  const cell = (v) => (v === null || v === undefined || v === '') ? '' : (Array.isArray(v) ? v.join('; ') : String(v));
  const respEntries = (r) => Object.entries(r.responses || {})
    .filter(([k, v]) => k !== 'guest_count' && v !== '' && v != null && (!Array.isArray(v) || v.length));

  const attendedCount = rsvps.filter((r) => r.attended).length;

  const toggleAttended = async (r) => {
    const next = !r.attended;
    setRsvps((prev) => prev.map((x) => (x.id === r.id ? { ...x, attended: next } : x)));
    try {
      await api.post(`/events/${event.id}/attendance`, { rsvp_id: r.id, attended: next });
    } catch (e) {
      toast.error('Could not update check-in');
      setRsvps((prev) => prev.map((x) => (x.id === r.id ? { ...x, attended: !next } : x)));
    }
  };

  const base = slugifyName(event.title);
  const dataFields = fields.filter((f) => f.key !== 'guest_count');
  const potluckFields = fields.filter((f) => f.share_responses || f.key === 'bringing');

  const exportRsvps = () => {
    const headers = [
      { key: 'name', label: 'Name' }, { key: 'email', label: 'Email' },
      { key: 'status', label: 'RSVP' }, { key: 'guests', label: 'Party size' },
      { key: 'attended', label: 'Attended' }, { key: 'message', label: 'Message' },
      ...dataFields.map((f) => ({ key: `f_${f.key}`, label: f.label })),
    ];
    const rows = rsvps.map((r) => ({
      name: cell(r.name), email: r.email, status: r.status, guests: r.guests || 1,
      attended: r.attended ? 'Yes' : 'No', message: cell(r.message),
      ...Object.fromEntries(dataFields.map((f) => [`f_${f.key}`, cell(r.responses?.[f.key])])),
    }));
    downloadCsv(`${base}-rsvps`, headers, rows);
    toast.success('Exported RSVPs');
  };

  const exportPotluck = () => {
    const headers = [
      { key: 'name', label: 'Name' }, { key: 'email', label: 'Email' }, { key: 'rsvp', label: 'RSVP' },
      ...potluckFields.map((f) => ({ key: `f_${f.key}`, label: f.label })),
    ];
    const rows = rsvps.filter((r) => r.status !== 'no').map((r) => ({
      name: cell(r.name), email: r.email, rsvp: r.status,
      ...Object.fromEntries(potluckFields.map((f) => [`f_${f.key}`, cell(r.responses?.[f.key])])),
    }));
    downloadCsv(`${base}-potluck`, headers, rows);
    toast.success('Exported potluck list');
  };

  const exportAttendance = () => {
    const headers = [
      { key: 'name', label: 'Name' }, { key: 'email', label: 'Email' },
      { key: 'status', label: 'RSVP' }, { key: 'guests', label: 'Party size' },
      { key: 'attended', label: 'Attended' },
    ];
    const rows = rsvps.map((r) => ({
      name: cell(r.name), email: r.email, status: r.status, guests: r.guests || 1,
      attended: r.attended ? 'Yes' : 'No',
    }));
    downloadCsv(`${base}-attendance`, headers, rows);
    toast.success('Exported attendance');
  };

  // Email analytics
  const aSent = invites.filter((i) => i.status === 'sent').length;
  const aDelivered = invites.filter((i) => i.delivered_at).length;
  const aOpened = invites.filter((i) => i.opened_at).length;
  const aClicked = invites.filter((i) => i.clicked_at).length;
  const aBounced = invites.filter((i) => i.bounced_at).length;
  const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);

  // Who hasn't responded (invited but no RSVP)
  const respondedEmails = new Set(rsvps.map((r) => (r.email || '').toLowerCase()));
  const noReply = invites.filter((i) => !respondedEmails.has((i.email || '').toLowerCase()));

  // Nudge (immediate reminder) for maybe / non-responders — opens the composer
  const markNudged = (emails) => {
    setNudged((prev) => { const n = new Set(prev); emails.forEach((e) => n.add((e || '').toLowerCase())); return n; });
  };

  return (
    <div className="space-y-6" data-testid="guests-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display text-xl font-semibold">Guests &amp; RSVPs</h2>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" className="gap-2" data-testid="guests-export-button"><Download className="h-4 w-4" /> Export CSV</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Download as CSV</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={exportRsvps} data-testid="export-rsvps">Guest list / RSVPs</DropdownMenuItem>
            <DropdownMenuItem onClick={exportPotluck} data-testid="export-potluck">Who&apos;s bringing what</DropdownMenuItem>
            <DropdownMenuItem onClick={exportAttendance} data-testid="export-attendance">Attendance report</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Email analytics */}
      <div className="rounded-2xl border border-border bg-card p-5" data-testid="email-analytics">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold">Email analytics</h3>
          <Button size="sm" variant="ghost" onClick={() => load(true)} disabled={refreshing} className="gap-1.5" data-testid="analytics-refresh">
            {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Refresh
          </Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="text-center rounded-xl bg-secondary/40 p-3"><div className="font-display text-2xl font-semibold">{aSent}</div><div className="text-xs text-muted-foreground mt-0.5">Sent</div></div>
          <div className="text-center rounded-xl bg-secondary/40 p-3"><div className="font-display text-2xl font-semibold text-primary">{aDelivered}</div><div className="text-xs text-muted-foreground mt-0.5">Delivered · {pct(aDelivered, aSent)}%</div></div>
          <div className="text-center rounded-xl bg-secondary/40 p-3"><div className="font-display text-2xl font-semibold text-accent">{aOpened}</div><div className="text-xs text-muted-foreground mt-0.5">Opened · {pct(aOpened, aSent)}%</div></div>
          <div className="text-center rounded-xl bg-secondary/40 p-3"><div className="font-display text-2xl font-semibold text-accent">{aClicked}</div><div className="text-xs text-muted-foreground mt-0.5">Clicked · {pct(aClicked, aSent)}%</div></div>
          <div className="text-center rounded-xl bg-secondary/40 p-3"><div className="font-display text-2xl font-semibold text-destructive">{aBounced}</div><div className="text-xs text-muted-foreground mt-0.5">Bounced</div></div>
        </div>
        {aOpened === 0 && aClicked === 0 && aSent > 0 && (
          <p className="text-xs text-muted-foreground mt-3">Per-recipient tracking is built in — opens register when a guest views the email and clicks register when they tap “View Event &amp; RSVP” or a calendar link. Stats update within moments of recipient activity (some email apps block the open pixel, so clicks are the most reliable signal).</p>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
        <div className="rounded-xl border border-border bg-card p-4"><div className="font-display text-2xl font-semibold text-primary">{yes.length}</div><div className="text-sm text-muted-foreground">Going</div></div>
        <div className="rounded-xl border border-border bg-card p-4"><div className="font-display text-2xl font-semibold text-accent">{maybe.length}</div><div className="text-sm text-muted-foreground">Maybe</div></div>
        <div className="rounded-xl border border-border bg-card p-4"><div className="font-display text-2xl font-semibold text-muted-foreground">{no.length}</div><div className="text-sm text-muted-foreground">Can&apos;t go</div></div>
        <div className="rounded-xl border border-border bg-card p-4"><div className="font-display text-2xl font-semibold text-muted-foreground">{noReply.length}</div><div className="text-sm text-muted-foreground">No reply</div></div>
        <div className="rounded-xl border border-border bg-card p-4"><div className="font-display text-2xl font-semibold text-accent">{headcount}</div><div className="text-sm text-muted-foreground">Headcount</div></div>
        <div className="rounded-xl border border-border bg-card p-4" data-testid="attended-stat"><div className="font-display text-2xl font-semibold text-primary">{attendedCount}</div><div className="text-sm text-muted-foreground">Checked in</div></div>
      </div>

      {/* Needs a nudge */}
      {(maybe.length > 0 || noReply.length > 0) && (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="needs-nudge-card">
          <h3 className="font-display text-lg font-semibold mb-1 flex items-center gap-2"><Bell className="h-[18px] w-[18px] text-primary" /> Needs a nudge</h3>
          <p className="text-sm text-muted-foreground mb-4">Send a friendly reminder to guests on the fence or yet to reply.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <div className="text-sm font-medium mb-2 flex items-center gap-1.5"><HelpCircle className="h-4 w-4 text-accent" /> Replied “maybe” ({maybe.length})</div>
              <NudgeList items={maybe} type="maybe" nudged={nudged} onCompose={(recipients) => setComposer({ recipients })} />
            </div>
            <div>
              <div className="text-sm font-medium mb-2 flex items-center gap-1.5"><Clock className="h-4 w-4 text-muted-foreground" /> Haven&apos;t replied ({noReply.length})</div>
              <NudgeList items={noReply} type="noreply" nudged={nudged} onCompose={(recipients) => setComposer({ recipients })} />
            </div>
          </div>
        </div>
      )}

      {/* What's being brought */}
      {sharedFields.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="bringing-summary">
          <h3 className="font-display text-lg font-semibold mb-3">What guests are bringing</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {sharedFields.map((f) => {
              const items = rsvps.filter((r) => r.status !== 'no' && r.responses && r.responses[f.key]);
              return (
                <div key={f.key}>
                  <div className="text-sm font-medium mb-2">{f.label}</div>
                  {items.length === 0 ? <p className="text-sm text-muted-foreground">Nothing yet.</p> : (
                    <ul className="space-y-1.5">
                      {items.map((r) => (
                        <li key={r.id} className="text-sm flex gap-2"><span className="text-foreground font-medium">{renderVal(r.responses[f.key])}</span><span className="text-muted-foreground">— {r.name || 'A guest'}</span></li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* RSVPs */}
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-2"><Users className="h-4 w-4 text-accent" /><h3 className="font-medium">RSVPs ({rsvps.length})</h3></div>
          <div className="max-h-[460px] overflow-auto">
            {rsvps.length === 0 ? <p className="p-6 text-sm text-muted-foreground text-center">No RSVPs yet. They&apos;ll appear here as guests respond.</p> :
              rsvps.map((r) => (
                <div key={r.id} className="px-4 py-3 border-b border-border last:border-0" data-testid="rsvp-item">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0"><div className="text-sm font-medium truncate">{r.name || r.email}</div><div className="text-xs text-muted-foreground">{r.email}{Number(r.guests) > 1 ? ` · party of ${r.guests}` : ''}</div></div>
                    <Badge variant="secondary" className={rsvpBadge(r.status)}>{r.status}</Badge>
                  </div>
                  {respEntries(r).length > 0 && (
                    <div className="mt-2 pl-0 space-y-1">
                      {respEntries(r).map(([k, v]) => (
                        <div key={k} className="text-xs"><span className="text-muted-foreground">{fieldLabel(k)}: </span><span className="text-foreground">{renderVal(v)}</span></div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>

        {/* Invites */}
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /><h3 className="font-medium">Invites sent ({invites.length})</h3></div>
          <div className="max-h-[460px] overflow-auto">
            {invites.length === 0 ? <p className="p-6 text-sm text-muted-foreground text-center">No invites sent yet.</p> :
              invites.map((iv) => (
                <div key={iv.id} className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-border last:border-0" data-testid="invite-row">
                  <div className="min-w-0"><div className="text-sm font-medium truncate">{iv.name || iv.email}</div><div className="text-xs text-muted-foreground truncate">{iv.email}</div></div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {iv.status === 'failed' ? <Badge variant="secondary" className="text-destructive">failed</Badge> :
                      iv.bounced_at ? <Badge variant="secondary" className="text-destructive">bounced</Badge> : (
                        <>
                          {iv.clicked_at ? <span title="Clicked" className="text-accent"><MousePointerClick className="h-4 w-4" /></span> : null}
                          {iv.opened_at ? <span title="Opened" className="text-accent"><Eye className="h-4 w-4" /></span> : null}
                          <Badge variant="secondary" className={iv.delivered_at ? 'text-primary' : 'text-muted-foreground'}>{iv.delivered_at ? 'delivered' : 'sent'}</Badge>
                        </>
                      )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      <NudgeComposer
        eventId={event.id}
        eventTitle={event.title}
        recipients={composer?.recipients || []}
        open={!!composer}
        onOpenChange={(o) => !o && setComposer(null)}
        onSent={markNudged}
      />
    </div>
  );
};
